import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthContextType, AuthState, User, TeamInfo } from '../types/auth';
import { apiClient } from '../services/api';
import { SecureTokenStorage } from '../utils/security';
// import { environmentProvider } from '../services/environmentProvider'; // Removed - not needed
import { simpleAuthService, ChangePasswordRequest } from '../services/simpleAuthService';
import { LoginRequest, RegisterRequest } from '../types/auth';

const initialState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  user: null,
  team: null,
  token: null,
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>(initialState);

  // Initialize auth state on mount
  useEffect(() => {
    // Removed mock environment check - using real API only

    // Development mode: bypass authentication
    if (import.meta.env.DEV && import.meta.env.VITE_BYPASS_AUTH === 'true') {
      setAuthState({
        isAuthenticated: true,
        isLoading: false,
        user: {
          id: 'dev-user-1',
          name: '開発用ユーザー',
          lineUserId: 'dev-line-user',
          role: 'admin',
        },
        team: {
          id: 'dev-team-1',
          name: '開発用チーム',
          role: 'admin',
          inviteCode: 'DEV123',
        },
        token: 'dev-token',
      });
      return;
    }

    initializeAuth();
    
    // Listen for logout events from API interceptor
    const handleLogout = () => {
      logout();
    };
    
    window.addEventListener('auth:logout', handleLogout);
    
    return () => {
      window.removeEventListener('auth:logout', handleLogout);
    };
  }, []);

  const initializeAuth = async () => {
    try {
      const token = localStorage.getItem('token');
      const refreshToken = localStorage.getItem('refreshToken');
      
      if (!token || !refreshToken) {
        setAuthState(prev => ({ ...prev, isLoading: false }));
        return;
      }

      // SecureTokenStorageにも設定
      SecureTokenStorage.setAccessToken(token);
      SecureTokenStorage.setRefreshToken(refreshToken);

      // Try to refresh token to validate it
      await refreshAuth();
    } catch (error) {
      console.error('Failed to initialize auth:', error);
      logout();
    }
  };

  const login = (token: string, refreshToken: string, user: User, team?: TeamInfo) => {
    console.log('🔍 Login function called with:', {
      token: token ? `${token.substring(0, 10)}...` : 'null',
      refreshToken: refreshToken ? `${refreshToken.substring(0, 10)}...` : 'null',
      user,
      team,
      timestamp: new Date().toISOString()
    });
    
    // SecureTokenStorageを使用してトークンを保存
    localStorage.setItem('token', token);
    localStorage.setItem('refreshToken', refreshToken);
    SecureTokenStorage.setAccessToken(token);
    SecureTokenStorage.setRefreshToken(refreshToken);
    SecureTokenStorage.setUserData(user);
    if (team) {
      SecureTokenStorage.setTeamData(team);
    }
    
    // SecureTokenStorageにも保存
    SecureTokenStorage.setAccessToken(token);
    SecureTokenStorage.setRefreshToken(refreshToken);
    
    // Set API client token
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    const newAuthState = {
      isAuthenticated: true,
      isLoading: false,
      user,
      team: team || null,
      token,
    };
    
    console.log('🔍 Setting new auth state:', newAuthState);
    setAuthState(newAuthState);
    
    // Force a small delay to ensure state is updated
    setTimeout(() => {
      console.log('🔍 Auth state after login:', {
        isAuthenticated: true,
        user: user?.id,
        team: team?.id,
        hasToken: !!token
      });
    }, 100);
  };

  const logout = async () => {
    try {
      // Note: logout endpoint not implemented yet
      // await apiClient.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage and state
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      SecureTokenStorage.clearAll();
      
      // Clear SecureTokenStorage
      SecureTokenStorage.clearAll();
      
      // Clear API client token
      delete apiClient.defaults.headers.common['Authorization'];
      
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        team: null,
        token: null,
      });
    }
  };

  const refreshAuth = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await apiClient.post('/auth/refresh', {
        refreshToken,
      });

      const { token: newToken, refreshToken: newRefreshToken } = response.data;
      
      // Update tokens
      localStorage.setItem('token', newToken);
      localStorage.setItem('refreshToken', newRefreshToken);
      SecureTokenStorage.setAccessToken(newToken);
      SecureTokenStorage.setRefreshToken(newRefreshToken);
      
      // Set API client token
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      
      setAuthState(prev => ({
        ...prev,
        token: newToken,
        isLoading: false,
      }));
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
      throw error;
    }
  };

  const loginWithLine = () => {
    const state = generateState();
    const lineLoginUrl = `https://access.line.me/oauth2/v2.1/authorize?` +
      `response_type=code&` +
      `client_id=${import.meta.env.VITE_LINE_CHANNEL_ID}&` +
      `redirect_uri=${encodeURIComponent(import.meta.env.VITE_LINE_REDIRECT_URI)}&` +
      `state=${state}&` +
      `scope=profile%20openid&` +
      `nonce=${generateState()}`;
    
    // Store state for verification
    storeState(state);
    
    // Debug logs
    console.log('🚀 LINE Login Debug:', {
      generatedState: state,
      stateLength: state.length,
      storedState: localStorage.getItem('line_state'),
      redirectUri: import.meta.env.VITE_LINE_REDIRECT_URI,
      channelId: import.meta.env.VITE_LINE_CHANNEL_ID,
      environment: import.meta.env.MODE,
      timestamp: new Date().toISOString(),
      loginUrl: lineLoginUrl
    });
    
    // Additional storage verification
    setTimeout(() => {
      console.log('🔍 Storage verification after 100ms:', {
        localStorage: localStorage.getItem('line_state'),
        sessionStorage: sessionStorage.getItem('line_state'),
        cookieExists: document.cookie.includes('line_state')
      });
    }, 100);
    
    window.location.href = lineLoginUrl;
  };

  const handleLineCallback = async (code: string, state: string) => {
    // Prevent duplicate processing of the same authorization code
    const codeKey = `line_code_${code}`;
    const existingStatus = localStorage.getItem(codeKey);
    
    if (existingStatus) {
      console.warn('Authorization code already processed:', existingStatus);
      if (existingStatus === 'processing') {
        throw new Error('認証処理中です。しばらくお待ちください。');
      } else if (existingStatus === 'completed') {
        throw new Error('この認証コードは既に使用済みです。再度ログインしてください。');
      } else if (existingStatus === 'failed') {
        throw new Error('この認証コードは処理に失敗しています。再度ログインしてください。');
      }
    }
    
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      
      // Mark this code as being processed immediately
      localStorage.setItem(codeKey, 'processing');
      
      // Debug logs
      const storedState = retrieveState();
      console.log('LINE Callback Debug:', {
        receivedState: state,
        storedState: storedState,
        stateMatch: state === storedState,
        code: code?.substring(0, 10) + '...',
        codeLength: code?.length || 0,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent
      });
      
      // TEMPORARY: Skip state verification for debugging
      console.log('🔧 TEMPORARY: Skipping state verification for debugging');
      console.log('State comparison:', {
        received: state,
        stored: storedState,
        match: state === storedState
      });
      
      // Clear stored state regardless
      clearState();
      
      // Exchange code for tokens (single attempt, no retries)
      console.log('🚀 Sending LINE callback request at:', new Date().toISOString());
      const response = await apiClient.post('/auth/line/callback', {
        code,
        state,
      });

      const data = response.data;
      
      // Mark code as successfully processed
      localStorage.setItem(codeKey, 'completed');
      console.log('✅ LINE callback successful at:', new Date().toISOString());
      
      if (data.isNewUser) {
        // New user needs to create or join team
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          team: null,
          token: null,
          tempToken: data.tempToken,
          lineUser: data.lineUser,
        });
      } else {
        // Existing user, log them in
        login(data.token, data.refreshToken, data.user, data.team);
      }
    } catch (error) {
      console.error('❌ LINE callback error at:', new Date().toISOString(), error);
      
      // Mark code as failed (prevent retries)
      localStorage.setItem(codeKey, 'failed');
      
      setAuthState(prev => ({ ...prev, isLoading: false }));
      
      // Re-throw the error with additional context for AuthCallback to handle
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any;
        const status = axiosError.response?.status;
        const errorData = axiosError.response?.data;
        
        console.error('API Error Details:', {
          status,
          statusText: axiosError.response?.statusText,
          data: errorData,
          headers: axiosError.response?.headers
        });
        
        // Create enhanced error object for AuthCallback
        const enhancedError = new Error(axiosError.message || 'Authentication failed');
        (enhancedError as any).response = axiosError.response;
        (enhancedError as any).status = status;
        (enhancedError as any).data = errorData;
        
        throw enhancedError;
      }
      
      // Network or other errors
      if (error && typeof error === 'object' && 'code' in error) {
        const networkError = error as any;
        if (networkError.code === 'NETWORK_ERROR' || networkError.message?.includes('Network Error')) {
          const enhancedError = new Error('ネットワークエラーが発生しました。インターネット接続を確認して再度お試しください。');
          (enhancedError as any).name = 'NetworkError';
          throw enhancedError;
        }
      }
      
      throw new Error('認証処理中にエラーが発生しました。再度ログインしてください。');
    }
  };

  const createTeam = async (teamName: string) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      
      const tempToken = authState.tempToken;
      if (!tempToken) {
        throw new Error('No temporary token available');
      }

      const response = await apiClient.post('/auth/teams', 
        { teamName },
        { headers: { Authorization: `Bearer ${tempToken}` } }
      );

      const { token, refreshToken, user, team } = response.data;
      login(token, refreshToken, user, team);
    } catch (error) {
      console.error('Create team error:', error);
      setAuthState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  };

  const joinTeam = async (inviteCode: string) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      
      const tempToken = authState.tempToken;
      if (!tempToken) {
        throw new Error('No temporary token available');
      }

      const response = await apiClient.post('/auth/teams/join', 
        { inviteCode },
        { headers: { Authorization: `Bearer ${tempToken}` } }
      );

      const { token, refreshToken, user, team } = response.data;
      login(token, refreshToken, user, team);
    } catch (error) {
      console.error('Join team error:', error);
      setAuthState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  };

  // シンプル認証用のメソッド
  const loginWithCredentials = async (credentials: LoginRequest) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      
      const response = await simpleAuthService.login(credentials);
      console.log('🔍 Login response:', response);
      console.log('🔍 User info:', response.user);
      console.log('🔍 Team info:', response.team);
      
      login(response.token, response.refreshToken, response.user, response.team);
    } catch (error) {
      setAuthState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  };

  const registerWithCredentials = async (userData: RegisterRequest) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      
      const response = await simpleAuthService.register(userData);
      login(response.token, response.refreshToken, response.user, response.team);
    } catch (error) {
      setAuthState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  };

  const changePassword = async (passwordData: ChangePasswordRequest) => {
    try {
      await simpleAuthService.changePassword(passwordData);
    } catch (error) {
      throw error;
    }
  };

  const createTeamWithSimpleAuth = async (teamName: string) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      
      console.log('🚀 Creating team:', teamName);
      const response = await simpleAuthService.createTeam(teamName);
      console.log('✅ Team creation response:', response);
      console.log('🔍 User info:', response.user);
      console.log('🔍 Team info:', response.team);
      
      login(response.token, response.refreshToken, response.user, response.team);
      console.log('✅ Login called after team creation');
    } catch (error) {
      console.error('❌ Team creation error:', error);
      setAuthState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  };

  const joinTeamWithSimpleAuth = async (inviteCode: string) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      
      const response = await simpleAuthService.joinTeam(inviteCode);
      login(response.token, response.refreshToken, response.user, response.team);
    } catch (error) {
      setAuthState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  };

  const value: AuthContextType = {
    ...authState,
    login,
    logout,
    refreshAuth,
    loginWithLine,
    handleLineCallback,
    createTeam,
    joinTeam,
    // シンプル認証用メソッド
    loginWithCredentials,
    registerWithCredentials,
    changePassword,
    createTeamWithSimpleAuth,
    joinTeamWithSimpleAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper function to generate state parameter
function generateState(): string {
  return Math.random().toString(36).substring(2, 15) + 
         Math.random().toString(36).substring(2, 15);
}

// Helper function to store state with multiple fallbacks
function storeState(state: string): void {
  try {
    // Primary: localStorage
    localStorage.setItem('line_state', state);
    
    // Fallback 1: sessionStorage
    sessionStorage.setItem('line_state', state);
    
    // Fallback 2: cookie (for cross-domain issues)
    document.cookie = `line_state=${state}; path=/; max-age=600; SameSite=Lax`;
    
    console.log('State stored successfully:', {
      localStorage: localStorage.getItem('line_state'),
      sessionStorage: sessionStorage.getItem('line_state'),
      cookie: document.cookie.includes('line_state')
    });
  } catch (error) {
    console.error('Failed to store state:', error);
  }
}

// Helper function to retrieve state with multiple fallbacks
function retrieveState(): string | null {
  try {
    // Try localStorage first
    let state = localStorage.getItem('line_state');
    if (state) return state;
    
    // Try sessionStorage
    state = sessionStorage.getItem('line_state');
    if (state) return state;
    
    // Try cookie
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'line_state') {
        return value;
      }
    }
    
    return null;
  } catch (error) {
    console.error('Failed to retrieve state:', error);
    return null;
  }
}

// Helper function to clear state from all storage
function clearState(): void {
  try {
    localStorage.removeItem('line_state');
    sessionStorage.removeItem('line_state');
    document.cookie = 'line_state=; path=/; max-age=0';
  } catch (error) {
    console.error('Failed to clear state:', error);
  }
}